import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';
import type { Project } from '@app/features/project';
import { projectsActions } from '@app/features/project';

export const ProjectTag = 'Project' as const;

export type ProjectFile = {
    id: number;
    name: string;
    metadata: {
        createdAt: string;
        updatedAt: string;
    };
    title: string | null;
};

export const getFileLabel = (name: string, title: string | null): string => title || name;

type ProjectFilesPayload = {
    projectId: string;
    selectionId: string;
};

type ProjectFilesResponse = {
    files: ProjectFile[];
    selectedIds: number[];
};

type ProjectFilesMutationPayload = {
    projectId: string | null;
    fileIds: number[];
};

type ProjectFilesMutationResponse = { selectionId: string };

type DeleteProjectFilesMutationPayload = {
    fileId?: number;
};

type DeleteProjectFileResponse = {
    result: string;
};

interface DeleteFilesMutationPayload {
    projectId: string;
    fileIds: number[];
}

export type ExportEmpty = 'empty' | 'base';

export type DownloadProjectFilesMutationPayload = {
    projectId: string;
    exportLangs: number[];
    fileIds: number[];
    exportEmptyAs: ExportEmpty;
    webhooks: string[];
};

type DownloadProjectFilesResponse = {
    meta: {
        statusUrl: string;
    };
};

export type DownloadStatus = 'created' | 'in_progress' | 'done' | 'failed';

type DownloadProjectFilesStatusResponse = {
    data: {
        status: DownloadStatus;
        downloadUrl: string | null;
    };
};

const projectsApi = createApi({
    reducerPath: 'lokaliseInternal_projects',
    baseQuery: fetchBaseQuery(),
    tagTypes: [ProjectTag, 'filenames'],
    endpoints: (builder) => ({
        getProjectStatistics: builder.query<GetProjectStatisticsResponse, GetProjectStatisticsRequest>({
            query: ({ projects }) => {
                const params = new URLSearchParams();
                projects.forEach((item, index) => {
                    params.append(`projects[${index}][id]`, item.id);
                    if (item.branch) {
                        params.append(`projects[${index}][branch]`, item.branch);
                    }
                });

                return {
                    method: 'GET',
                    url: '/projects/statistics',
                    params,
                };
            },
            providesTags: [ProjectTag],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(projectsActions.updateProjectList(data));
                    // eslint-disable-next-line no-empty
                } catch (err) {}
            },
        }),
        getProjectFiles: builder.query<ProjectFilesResponse, ProjectFilesPayload>({
            query: ({ projectId, selectionId }) => ({
                method: 'GET',
                url: `/projects/${projectId}/files-selection/`,
                params: { selectionId },
            }),
            providesTags: ['filenames'],
        }),
        selectProjectFile: builder.mutation<ProjectFilesMutationResponse, ProjectFilesMutationPayload>({
            query: ({ projectId, fileIds }) => ({
                url: `/projects/${projectId}/files-selection/`,
                method: 'POST',
                body: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    file_ids: fileIds,
                },
            }),
        }),
        deleteProjectFile: builder.mutation<DeleteProjectFileResponse, DeleteProjectFilesMutationPayload>({
            query: ({ fileId }) => ({
                url: `/filenames/${fileId}/remove`,
                method: 'POST',
            }),
            invalidatesTags: ['filenames'],
        }),
        deleteProjectFiles: builder.mutation<void, DeleteFilesMutationPayload>({
            query: ({ projectId, fileIds }) => ({
                url: '/filenames',
                method: 'DELETE',
                body: {
                    projectId,
                    fileIds,
                },
            }),
            invalidatesTags: ['filenames'],
        }),
        downloadProjectFiles: builder.mutation<DownloadProjectFilesResponse, DownloadProjectFilesMutationPayload>({
            query: ({ projectId, exportLangs, fileIds, exportEmptyAs, webhooks }) => ({
                url: `/projects/${projectId}/files/download`,
                method: 'POST',
                body: {
                    exportLangs,
                    fileIds,
                    exportEmptyAs,
                    webhooks,
                },
            }),
            invalidatesTags: ['filenames'],
        }),
        getDownloadProjectFilesStatus: builder.query<DownloadProjectFilesStatusResponse, { statusUrl: string }>({
            query: ({ statusUrl }) => ({
                url: statusUrl,
                method: 'GET',
            }),
        }),
    }),
});

export type GetProjectStatisticsRequest = {
    projects: ProjectReference[];
};

export type ProjectReference = {
    id: string;
    branch?: string | null;
};

export type GetProjectStatisticsResponse = Project[];

export default projectsApi;

export const {
    useLazyGetProjectStatisticsQuery,
    useGetProjectFilesQuery,
    useSelectProjectFileMutation,
    useDeleteProjectFileMutation,
    useDeleteProjectFilesMutation,
    useDownloadProjectFilesMutation,
    useLazyGetDownloadProjectFilesStatusQuery,
} = projectsApi;
